import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Spin, Alert, message, DatePicker } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getSocialPost,
  createSocialPost,
  updateSocialPost,
  type CreateSocialPostDto,
  type UpdateSocialPostDto,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;
const POST_TYPE_OPTIONS = ['text', 'image', 'video', 'link'];

interface PostFormValues {
  account_id: string;
  content: string;
  post_type: string;
  media_urls?: string;
  scheduled_at?: dayjs.Dayjs;
  tags?: string[];
}

export default function SocialPostForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<PostFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getSocialPost(id)
      .then((post) => {
        if (post.status === 'published') {
          message.warning(t('app.error'));
          navigate(`/social/posts/${id}`);
          return;
        }
        form.setFieldsValue({
          account_id: post.account_id,
          content: post.content,
          post_type: post.post_type || 'text',
          media_urls: post.media_urls?.join(', ') || undefined,
          scheduled_at: post.scheduled_at ? dayjs(post.scheduled_at) : undefined,
          tags: (post.tags?.length ?? 0) > 0 ? post.tags : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: PostFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const mediaUrls = values.media_urls
        ? values.media_urls.split(',').map((u) => u.trim()).filter(Boolean)
        : undefined;

      if (isEdit && id) {
        const dto: UpdateSocialPostDto = {
          content: values.content,
          post_type: values.post_type,
          media_urls: mediaUrls,
          scheduled_at: values.scheduled_at?.toISOString(),
          tags: values.tags,
        };
        await updateSocialPost(id, dto);
        message.success(t('social.update_post_success'));
        navigate(`/social/posts/${id}`);
      } else {
        const dto: CreateSocialPostDto = {
          account_id: values.account_id,
          content: values.content,
          post_type: values.post_type,
          media_urls: mediaUrls,
          scheduled_at: values.scheduled_at?.toISOString(),
          tags: values.tags,
        };
        const created = await createSocialPost(dto);
        message.success(t('social.create_post_success'));
        navigate(`/social/posts/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('social_posts', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/social/posts')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('social.edit_post_title') : t('social.create_post_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ post_type: 'text' }}
        style={{ maxWidth: 700 }}
      >
        <Form.Item
          name="account_id"
          label={t('social.field.account')}
          rules={[{ required: !isEdit, message: t('social.validation.account_id_required') }]}
        >
          <Input disabled={isEdit} placeholder={t('social.field.account_id')} />
        </Form.Item>

        <Form.Item
          name="content"
          label={t('social.field.content')}
          rules={[
            { required: true, message: t('social.validation.content_required') },
            { max: 10000, message: t('social.validation.content_max_length') },
          ]}
        >
          <TextArea rows={6} />
        </Form.Item>

        <Form.Item
          name="post_type"
          label={t('social.field.post_type')}
          rules={[{ required: true, message: t('social.validation.post_type_required') }]}
        >
          <Select>
            {POST_TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`social.post_type.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="scheduled_at" label={t('social.field.scheduled_at')}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="media_urls" label={t('social.field.media_urls')}>
          <TextArea rows={2} placeholder={t('social.field.media_urls')} />
        </Form.Item>

        <Form.Item name="tags" label={t('social.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/social/posts')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
